import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { tokens } from '../utils/api';
import toast from 'react-hot-toast';

const TxIcon = ({ amount }) => (
  <span style={{ fontSize: '18px' }}>{amount > 0 ? '⬆️' : '⬇️'}</span>
);

const typeLabel = {
  WELCOME_BONUS:          'Welcome bonus',
  JOB_POSTED:             'Job posted',
  JOB_APPLY:              'Applied to job',
  CONTACT_VIEW:           'Viewed contact',
  SEEK_AD_PUBLISHED:      'Seek ad published',
  TOKEN_REQUEST_APPROVED: 'Token request approved',
};

export default function Wallet() {
  const { t, user } = useApp();
  const navigate     = useNavigate();
  const [balance,   setBalance]   = useState(null);
  const [history,   setHistory]   = useState([]);
  const [pending,   setPending]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [showForm,  setShowForm]  = useState(false);
  const [requesting,setRequesting]= useState(false);
  const [form, setForm] = useState({ amount: '', reason: '' });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    tokens.balance()
      .then(r => {
        setBalance(r.balance);
        setHistory(r.transactions ?? []);
      })
      .catch(() => toast.error(t('error_occurred')))
      .finally(() => setLoading(false));

    tokens.myRequests()
      .then(r => {
        const pending = (r.requests ?? []).find(req => req.status === 'PENDING');
        setPending(pending ?? null);
      })
      .catch(() => {});
  }, [user, navigate, t]);

  const handleRequest = async e => {
    e.preventDefault();
    const amount = parseInt(form.amount, 10);
    if (!amount || amount < 1 || amount > 100) {
      toast.error('Amount must be between 1 and 100.'); return;
    }
    if (!form.reason.trim()) { toast.error('Please provide a reason.'); return; }
    setRequesting(true);
    try {
      await tokens.request({ amount, reason: form.reason });
      toast.success(t('token_request_sent'));
      setShowForm(false);
      setForm({ amount: '', reason: '' });
      const r = await tokens.myRequests();
      const p = (r.requests ?? []).find(req => req.status === 'PENDING');
      setPending(p ?? null);
    } catch (err) {
      toast.error(err.error || t('error_occurred'));
    } finally { setRequesting(false); }
  };

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;

  const COSTS = [
    ['Post a job',        5],
    ['Publish seek ad',   3],
    ['Apply to job',      1],
    ['View contact',      2],
  ];

  return (
    <div className="container-sm" style={{ paddingTop: '36px', paddingBottom: '60px' }}>
      <h1 style={{ fontSize: '24px', fontWeight: 700, marginBottom: '24px' }}>{t('nav_wallet')}</h1>

      {/* Balance card */}
      <div className="card" style={{ background: 'var(--brand)', border: 'none', marginBottom: '16px', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(circle at 80% 20%, rgba(255,255,255,0.08), transparent 60%)', pointerEvents: 'none' }} />
        <div style={{ position: 'relative' }}>
          <div style={{ fontSize: '13px', color: 'rgba(255,255,255,0.7)', marginBottom: '4px' }}>{t('token_balance')}</div>
          <div style={{ fontSize: '52px', fontWeight: 700, color: '#fff', lineHeight: 1, marginBottom: '4px' }}>{balance ?? '—'}</div>
          <div style={{ fontSize: '14px', color: 'rgba(255,255,255,0.7)' }}>{t('tokens')}</div>
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-sm"
              style={{ background: 'rgba(255,255,255,0.2)', color: '#fff', backdropFilter: 'blur(4px)' }}
              onClick={() => setShowForm(s => !s)}
              disabled={!!pending}
            >
              {pending ? '⏳ Request pending' : `+ ${t('request_tokens')}`}
            </button>
          </div>
        </div>
      </div>

      {/* Pending notice */}
      {pending && (
        <div className="card" style={{ background: 'var(--amber-bg)', border: '1px solid var(--amber)', marginBottom: '16px', padding: '14px 16px' }}>
          <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--amber-text)' }}>⏳ Token request pending</div>
          <div style={{ fontSize: '12px', color: 'var(--text2)', marginTop: '4px' }}>
            Requested <strong>{pending.amount}</strong> tokens — awaiting moderator approval.
          </div>
          <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '4px' }}>Reason: {pending.reason}</div>
        </div>
      )}

      {/* Request form */}
      {showForm && !pending && (
        <form className="card" style={{ marginBottom: '16px' }} onSubmit={handleRequest}>
          <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>{t('request_tokens')}</div>
          <div className="form-group">
            <label className="form-label">Amount (1–100)</label>
            <input className="form-input" type="number" required min="1" max="100"
              value={form.amount} onChange={e => setForm(f => ({ ...f, amount: e.target.value }))}
              placeholder="e.g. 20" />
          </div>
          <div className="form-group">
            <label className="form-label">Reason</label>
            <input className="form-input" required minLength={5}
              value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="e.g. I need to post 4 jobs this week" />
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button type="button" className="btn btn-outline btn-sm" onClick={() => setShowForm(false)}>Cancel</button>
            <button type="submit" className="btn btn-primary btn-sm" disabled={requesting}>
              {requesting ? <span className="spinner" style={{ width: 14, height: 14 }} /> : 'Send request'}
            </button>
          </div>
        </form>
      )}

      {/* Token cost table */}
      <div className="card" style={{ marginBottom: '20px', padding: '14px 16px' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '10px', color: 'var(--text2)' }}>Token costs</div>
        {COSTS.map(([label, cost]) => (
          <div key={label} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
            <span style={{ color: 'var(--text2)' }}>{label}</span>
            <span style={{ fontWeight: 600 }}>{cost} tokens</span>
          </div>
        ))}
      </div>

      {/* Transaction history */}
      <div className="card">
        <div style={{ fontSize: '15px', fontWeight: 600, marginBottom: '14px' }}>{t('transaction_history')}</div>
        {history.length === 0 ? (
          <div className="empty-state" style={{ padding: '24px' }}>No transactions yet</div>
        ) : (
          history.map((tx, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 0', borderBottom: i < history.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <TxIcon amount={tx.amount} />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '13px', fontWeight: 500 }}>
                  {typeLabel[tx.type] ?? tx.type ?? tx.note ?? 'Transaction'}
                </div>
                {tx.note && tx.note !== tx.type && (
                  <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '1px' }}>{tx.note}</div>
                )}
                <div style={{ fontSize: '11px', color: 'var(--text3)', marginTop: '2px' }}>
                  {new Date(tx.created_at).toLocaleString('en-BD', { dateStyle: 'medium', timeStyle: 'short' })}
                </div>
              </div>
              <div style={{ fontSize: '15px', fontWeight: 700, color: tx.amount > 0 ? 'var(--brand)' : 'var(--red-text)' }}>
                {tx.amount > 0 ? `+${tx.amount}` : tx.amount}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
